import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getClinicId } from "../../../utils/config";
import FormatUrl from "../../../utils/UrlFormatter";
import "../styles/AdminBookingModal.css";

const AdminBookingModal = ({
  slotInfo,
  selectedPatient,
  onClose,
  isLoading = false,
  resetAllFields,
}) => {
  const { t } = useTranslation();
  const [csrfToken, setCsrfToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const fullName = `${selectedPatient?.first_name || ""} ${selectedPatient?.last_name || ""}`.trim();
  const discipline = slotInfo?.discipline;

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const res = await fetch(FormatUrl("/csrf-token/"), {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        const data = await res.json();
        setCsrfToken(data.csrfToken);
      } catch {
        toast.error(t("admin_sched.csrf_error"));
      }
    };
    fetchCsrfToken();
  }, [t]);

  const getTimeRange = () => {
    if (!slotInfo?.slot?.start || !slotInfo?.slot?.end) return "—";
    const start = new Date(slotInfo.slot.start).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    const end = new Date(slotInfo.slot.end).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${start} – ${end}`;
  };

  // const practitionerType = slotInfo?.slot?.practitioner?.is_registered
  //   ? t("admin_sched.licensed")
  //   : t("admin_sched.non_licensed");

  // const baseFeeRaw = slotInfo?.slot?.practitioner?.base_fee;
  // const formattedFee =
  //   baseFeeRaw !== undefined && baseFeeRaw !== null
  //     ? `$${parseFloat(baseFeeRaw).toFixed(2)} ${t("admin_sched.plus_tax")}`
  //     : "—";
  const baseFeeRaw = slotInfo?.slot?.practitioner?.base_fee;
  const formattedFee =
    baseFeeRaw !== undefined && baseFeeRaw !== null
      ? `$${parseFloat(baseFeeRaw).toFixed(2)}${discipline === "Acupuncture" ? "" : ` ${t("admin_sched.plus_tax")}`}`
      : "—";


  const handleConfirm = async () => {
    if (!csrfToken) {
      toast.error(t("admin_sched.csrf_missing"));
      return;
    }

    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append("clinic_id", getClinicId(selectedPatient));
      formData.append("patient_id", selectedPatient?.id);
      formData.append("slot_id", slotInfo?.slot?.id);
      formData.append("practitioner_id", slotInfo?.slot?.practitioner?.id);

      const res = await fetch(FormatUrl("/create-slot/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok || data.status === "failure") {
        toast.error(t("admin_sched.booking_failed"));
        return;
      }

      toast.success(t("admin_sched.booking_confirmed"));
      setSuccess(true);

      setTimeout(() => {
        resetAllFields?.();
        onClose();
      }, 3000);
    } catch (err) {
      console.error("Booking error:", err);
      toast.error(t("admin_sched.booking_generic_error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading || !slotInfo?.slot) {
    return (
      <div className="modal-overlay_v3">
        <div className="modal-content_v3">
          <p className="loading-spinner_v3">
            {t("admin_sched.loading_booking_details")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay_v3">
      <div className="modal-content_v3">
        {success ? (
          <div className="pb-success-message">
            <h3>{t("admin_sched.booking_success_title")}</h3>
            <p>{t("admin_sched.booking_success_message")}</p>
          </div>
        ) : (
          <>
            <h3>{t("admin_sched.confirm_booking_title")}</h3>

            <div className="modal-summary_v3">
              <div className="modal-row_v3">
                <strong>{t("admin_sched.customer_label")}:</strong>
                <span>{fullName || "N/A"}</span>
              </div>
              <div className="modal-row_v3">
                <strong>{t("admin_sched.practitioner_label")}:</strong>
                <span>{slotInfo?.practitionerName || "N/A"}</span>
              </div>
              {/* <div className="modal-row_v3">
                <strong>{t("admin_sched.practitioner_type_label")}:</strong>
                <span>{practitionerType}</span>
              </div> */}
              <div className="modal-row_v3">
                <strong>{t("admin_sched.appointment_fee_label")}:</strong>
                <span>{formattedFee}</span>
              </div>
              <div className="modal-row_v3">
                <strong>{t("admin_sched.date_label")}:</strong>
                <span>{slotInfo?.date || "N/A"}</span>
              </div>
              <div className="modal-row_v3">
                <strong>{t("admin_sched.time_label")}:</strong>
                <span>{getTimeRange()}</span>
              </div>
            </div>

            <div className="modal-actions_v3">
              <button className="cancel-btn_v3" onClick={onClose} disabled={submitting}>
                {t("admin_sched.cancel_button")}
              </button>
              <button className="confirm-btn_v3" onClick={handleConfirm} disabled={submitting}>
                {submitting ? t("admin_sched.booking_loading") : t("admin_sched.confirm_button")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminBookingModal;
